﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import type { ApiError, EmployeePaidHours } from '@pms/shared';
import { paidHoursService } from '@/services/paid-hours.service';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/tasks/DateRangePicker';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ThumbsDown,
  RotateCcw,
  X,
  UserCircle2,
} from 'lucide-react';

// ── Status badge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: EmployeePaidHours['status'] }) {
  const cfg = {
    pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50' },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900/50' },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900/50' },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

// ── Reject dialog ──────────────────────────────────────────────────────────────

function RejectDialog({
  record,
  onConfirm,
  onCancel,
  submitting,
}: {
  record: EmployeePaidHours;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-popover shadow-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground">Reject Paid Hours</h3>
          <button
            onClick={onCancel}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Rejecting paid hours for{' '}
          <strong className="text-foreground">{[record.first_name, record.last_name].filter(Boolean).join(' ')}</strong> on{' '}
          {new Date(record.attendance_date + 'T00:00:00').toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
          })}.
        </p>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Rejection Reason <span className="text-destructive">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for rejection..."
            rows={3}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button
            size="sm"
            disabled={submitting || !reason.trim()}
            className="bg-rose-500 hover:bg-rose-600 text-white gap-1.5 dark:bg-rose-600 dark:hover:bg-rose-500"
            onClick={() => onConfirm(reason.trim())}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Tab ───────────────────────────────────────────────────────────────────

const PAGE_LIMIT = 20;

export function PaidHoursTab() {
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('');
  const [search,       setSearch]       = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [page,         setPage]         = useState(1);
  const [rejectTarget, setRejectTarget] = useState<EmployeePaidHours | null>(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['paid-hours', statusFilter, search, dateFrom, dateTo, page],
    queryFn: () =>
      paidHoursService.list({
        status:    statusFilter || undefined,
        q:         search || undefined,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        page,
        limit:     PAGE_LIMIT,
      }),
  });

  const records     = data?.data ?? [];
  const pagination  = data?.pagination;
  const totalPages  = pagination?.totalPages ?? 1;

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['paid-hours'] });
    qc.invalidateQueries({ queryKey: ['payroll', 'salary'] });
    qc.invalidateQueries({ queryKey: ['payroll', 'salary', 'me'] });
  }

  const approveMutation = useMutation({
    mutationFn: (id: string) => paidHoursService.approve(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Paid hours approved');
    },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to approve';
      toast.error(msg);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      paidHoursService.reject(id, reason),
    onSuccess: () => {
      invalidateAll();
      setRejectTarget(null);
      toast.success('Paid hours rejected');
    },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to reject';
      toast.error(msg);
      setRejectSubmitting(false);
    },
  });

  const pendingMutation = useMutation({
    mutationFn: (id: string) => paidHoursService.moveToPending(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Moved back to pending');
    },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to update';
      toast.error(msg);
    },
  });

  function handleRejectConfirm(reason: string) {
    if (!rejectTarget) return;
    setRejectSubmitting(true);
    rejectMutation.mutate({ id: rejectTarget.id, reason });
  }

  function formatDate(iso: string) {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  }

  const statusOptions: Array<{ value: '' | 'pending' | 'approved' | 'rejected'; label: string }> = [
    { value: '',         label: 'All Status' },
    { value: 'pending',  label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="space-y-4">
      {/* Section title */}
      <div className="flex items-center gap-2">
        <UserCircle2 className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Company Paid Hours</h2>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search by employee name or code…"
          containerClassName="w-full sm:max-w-xs"
        />

        {/* Status filter */}
        <Select
          value={statusFilter || '__all__'}
          onValueChange={(v) => { setStatusFilter(v === '__all__' ? '' : (v as typeof statusFilter)); setPage(1); }}
        >
          <SelectTrigger className="h-9 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((o) => (
              <SelectItem key={o.value || '__all__'} value={o.value || '__all__'}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
          onDateToChange={(v) => { setDateTo(v); setPage(1); }}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-red-200 bg-red-50 py-8 text-sm text-red-600">
          <span>Failed to load paid hours</span>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center">
          <UserCircle2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No paid hours records found</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border/60 bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {records.map((r) => {
                  const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
                  return (
                    <tr key={r.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{name}</p>
                        {r.employee_code && (
                          <p className="text-xs text-muted-foreground">{r.employee_code}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {formatDate(r.attendance_date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-teal-700">{parseFloat(r.hours).toFixed(1)}h</span>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <p className="truncate text-muted-foreground text-xs" title={r.reason}>{r.reason}</p>
                        {r.rejection_reason && (
                          <p className="truncate text-xs text-red-500 mt-0.5" title={r.rejection_reason}>
                            ↳ {r.rejection_reason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {r.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => approveMutation.mutate(r.id)}
                                disabled={approveMutation.isPending}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => setRejectTarget(r)}
                              >
                                <ThumbsDown className="h-3.5 w-3.5" /> Reject
                              </Button>
                            </>
                          )}
                          {r.status !== 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 text-muted-foreground hover:bg-muted/40"
                              onClick={() => pendingMutation.mutate(r.id)}
                              disabled={pendingMutation.isPending}
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Revert
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {records.map((r) => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
              return (
                <div key={r.id} className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-foreground">{name}</p>
                      {r.employee_code && <p className="text-xs text-muted-foreground">{r.employee_code}</p>}
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-medium">{formatDate(r.attendance_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Hours</p>
                      <p className="font-semibold text-teal-700">{parseFloat(r.hours).toFixed(1)}h</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{r.reason}</p>
                  {r.rejection_reason && (
                    <p className="text-xs text-red-500">↳ {r.rejection_reason}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {r.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => approveMutation.mutate(r.id)} disabled={approveMutation.isPending}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setRejectTarget(r)}>
                          <ThumbsDown className="h-3.5 w-3.5" /> Reject
                        </Button>
                      </>
                    )}
                    {r.status !== 'pending' && (
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-muted-foreground hover:bg-muted/40"
                        onClick={() => pendingMutation.mutate(r.id)} disabled={pendingMutation.isPending}>
                        <RotateCcw className="h-3.5 w-3.5" /> Revert
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Page {pagination?.page ?? 1} of {totalPages} · {pagination?.total ?? 0} records
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline" size="icon"
                  aria-label="Previous page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4 shrink-0" />
                </Button>
                <Button
                  variant="outline" size="icon"
                  aria-label="Next page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4 shrink-0" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Reject dialog */}
      {rejectTarget && (
        <RejectDialog
          record={rejectTarget}
          onConfirm={handleRejectConfirm}
          onCancel={() => { setRejectTarget(null); setRejectSubmitting(false); }}
          submitting={rejectSubmitting}
        />
      )}
    </div>
  );
}
